import { Credit, type OnDemandContent, type ContentProperties } from "@ca.vmedia.tv/domains";
import { type CardData, CreditCardData, EpisodeCardData, RelatedOnDemandCardData } from "@/components/Cards/types";
import { AppConstants } from "@/common";
import { Services } from "@ca.vmedia.tv/services";
import { useDetailPageStore } from "@/stores/DetailPage";
import { useDetailShowPageStore } from "@/stores/DetailShowPage";
import { Logger } from '@/utils';
import { useFetchRelatedContent } from '@/composables/useFetchRelatedContent';
import { useEmptyLine } from "@/composables/useEmptyLine";
import type { AxiosError } from 'axios';
import type { ExposeInterface } from './types';

export const useLinesDetailShowStore = defineStore('LinesDetailShow', (): ExposeInterface => {    
    const { 
        emptyOnDemandContent
    } = useEmptyLine();

    const DetailPageStore = useDetailPageStore();
    const {
        getCurrentItemDetail,
    } = storeToRefs(DetailPageStore);

    const DetailShowPage = useDetailShowPageStore();
    const {
        getCurrentSeason: _currentSeason,
        getEpisodes: _episodes,
        getEpisodesProperties: _episodesProperties
    } = storeToRefs(DetailShowPage);

    const _cast: Ref<Credit[] | null> = ref(null);
    const fetchCast = async (): Promise<void> => {
        try {
            if (!getCurrentItemDetail.value?.id || !getCurrentItemDetail.value?.contentType) {
                throw new Error(`No item detail: ${getCurrentItemDetail.value}`);
            }
            const credits = await Services.Instance.credits.getCredits(getCurrentItemDetail.value?.id, getCurrentItemDetail.value?.contentType);
            _cast.value = credits.filter((elem, index, self) => self.findIndex((credit: Credit) => Credit.getId(credit) === Credit.getId(elem)) === index);
        } catch (error) {
            _cast.value = [];
            Logger.captureError(error as AxiosError | Error);
        }
    };
    const getCardsCast: ComputedRef<CardData[] | null> = computed(() => {
        const data = _cast.value;

        if (data === null) {
            return null;
        }

        if (data.length === 0) {
            return [];
        }

        return data.map((item) => new CreditCardData(item));
    }); 

    const _relatedContent: Ref<OnDemandContent | null> = ref(null);
    const fetchRelatedContent = async (): Promise<void> => {
        try {
            _relatedContent.value = await useFetchRelatedContent(getCurrentItemDetail.value, AppConstants.ItemsInLine);
        } catch (error) {
            _relatedContent.value = emptyOnDemandContent();
            Logger.captureError(error as AxiosError | Error);
        }
    };
    const getCardsRelatedContent: ComputedRef<CardData[] | null> = computed(() => {
        if (_relatedContent.value === null) {
            return null;
        }

        if (!_relatedContent.value.items || _relatedContent.value.items.length === 0) {
            return [];
        }

        return _relatedContent.value.items.map((item) => new RelatedOnDemandCardData(item, String(getCurrentItemDetail.value?.id)));
    }); 

    const getCardsEpisodes: ComputedRef<CardData[] | null> = computed(() => {
        const data = _episodes.value;

        if (data === null || data.items === null) {
            return null;
        }

        if (data.items.length === 0) {
            return [];
        }

        const items = data.items.map((OnDemandItem, index) => new EpisodeCardData(OnDemandItem, index));

        if (_episodesProperties.value){
            items.forEach((episode: EpisodeCardData) => {
                const id = episode.getPlayItem().id;
                if (_episodesProperties.value) {
                    const prop = _episodesProperties.value.find((propItem: ContentProperties) => propItem.id == id);
                    episode.setPosition(prop?.streamPosition as number);
                }
            });
        }
        return items;
    });

    const _counterRows: Ref<number> = ref(AppConstants.DetailCounterLines);
    const getCounterLinesDetailShow: ComputedRef<number> = computed(() => _counterRows.value);
    const _clearCounter = (): void => {
        _counterRows.value = AppConstants.DetailCounterLines;
    };
    const increaseCounterLinesDetailShow = (): void => {
        _counterRows.value += 1;
    };

    const clearAllLinesDetailShow = (): void => {
        _clearCounter();
        _cast.value = null;
        _relatedContent.value = null;
    };

    return {
        getCardsEpisodes,
        getCardsCast,
        getCardsRelatedContent,
        getCounterLinesDetailShow,
        fetchCast,
        fetchRelatedContent,
        increaseCounterLinesDetailShow,
        clearAllLinesDetailShow
    };
});
